import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  ChevronRight,
  UserPlus,
  LayoutDashboard,
  Users,
  FolderOpen,
  LogOut,
  Menu,
  X,
  Filter,
  Calendar,
  Target,
  Settings,
  Edit,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { BH_OFFICES, BH_LOCATION_OPTIONS } from "@/constants/locations";
import { bhLocationShort } from "@/constants/lenders";
import { ReadOnly } from "@/components/ReadOnly";
import { LocationReportButton } from "@/components/LocationReportButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Status Badge
const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Referral Submitted": "bg-blue-100 text-blue-800 border-blue-200",
    "Intake Complete": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Credit Active": "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress": "bg-purple-100 text-purple-800 border-purple-200",
    "Ready for Lender": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Loan Ready": "bg-green-100 text-green-800 border-green-200",
    default: "bg-slate-100 text-slate-800 border-slate-200",
  };

  return (
    <Badge variant="outline" className={statusStyles[status] || statusStyles.default}>
      {status || "Unknown"}
    </Badge>
  );
};

const AllClients = ({ isAdmin }) => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);

  // Initialize filters from URL search params (dashboard chart click-through)
  const initFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      status: params.get("status") || "",
      location: params.get("location") || "",
      counselor: params.get("counselor") || "",
      affiliate: params.get("affiliate") || "",
      leadStatus: params.get("lead_status") || "",
      loanReadyStage: params.get("loan_ready_stage") || "",
      rounds: params.get("rounds") || "",
      loanReadyGrats: params.get("loan_ready_grats") || params.get("client_progress") || "",
      accountStatus: params.get("account_status") || "",
      salesManager: params.get("sales_manager") || "",
      bhAdmin: params.get("bh_admin") || "",
    };
  };

  const [filters, setFilters] = useState(initFiltersFromUrl);
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    counselors: [],
    affiliates: [],
    leadStatuses: [],
    loanReadyStages: [],
    rounds: [],
    loanReadyGrats: [],
    accountStatuses: [],
    salesManagers: [],
    bhAdmins: [],
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debounceRef = useRef(null);
  
  // Debounced search - 250ms delay
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
    }, 250);
  }, []);
  
  // Edit/Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const canEditClients = ["CREDITFIRMINC_ADMIN", "CREDIT_COUNSELOR"].includes(user?.role);
  const [editLenders, setEditLenders] = useState([]);
  const [editCounselors, setEditCounselors] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [newClient, setNewClient] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    address1: "",
    city: "",
    state: "",
    postal_code: "",
    betenbough_location: "",
    betenbough_sales_rep: "",
    betenbough_client_id_number: "",
    betenbough_affiliated_lender: "",
    betenbough_assigned_credit_counselor: "",
    betenbough_special_notes_or_credit_strategy: "",
    betenbough_deadline_date_for_loan_ready: "",
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // One fetch per filters/page change; a filter change on page > 1 first resets to page 1 (that change triggers the fetch)
  const filtersKey = JSON.stringify(filters);
  const lastFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (lastFiltersKey.current !== filtersKey) {
      lastFiltersKey.current = filtersKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    fetchClients();
  }, [filtersKey, currentPage]);

  const fetchFilterOptions = async () => {
    try {
      const response = await authFetch(`${API}/lookup/all-filters`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions({
          locations: BH_OFFICES,
          counselors: data.counselors || [],
          affiliates: data.affiliates || [],
          leadStatuses: data.lead_statuses || [],
          loanReadyStages: data.loan_ready_stages || [],
          rounds: data.rounds || [],
          loanReadyGrats: data.loan_ready_grats || [],
          accountStatuses: data.account_statuses || [],
          salesManagers: data.sales_managers || [],
          bhAdmins: data.bh_admins || [],
        });
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchSeq = useRef(0);
  const fetchClients = async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.location) params.append("location", filters.location);
      if (filters.counselor) params.append("counselor", filters.counselor);
      if (filters.affiliate) params.append("affiliate", filters.affiliate);
      if (filters.leadStatus) params.append("lead_status", filters.leadStatus);
      if (filters.loanReadyStage) params.append("loan_ready_stage", filters.loanReadyStage);
      if (filters.rounds) params.append("rounds", filters.rounds);
      if (filters.loanReadyGrats) params.append("loan_ready_grats", filters.loanReadyGrats);
      if (filters.accountStatus) params.append("account_status", filters.accountStatus);
      if (filters.salesManager) params.append("sales_manager", filters.salesManager);
      if (filters.bhAdmin) params.append("bh_admin", filters.bhAdmin);
      params.append("skip", ((currentPage - 1) * pageSize).toString());
      params.append("limit", pageSize.toString());

      const response = await authFetch(`${API}/clients?${params}`, {
        credentials: "include",
      });
      if (seq !== fetchSeq.current) return; // a newer request superseded this one
      if (response.ok) {
        const data = await response.json();
        if (seq !== fetchSeq.current) return;
        setClients(data.clients);
        setTotalClients(data.total);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      location: "",
      counselor: "",
      affiliate: "",
      leadStatus: "",
      loanReadyStage: "",
      rounds: "",
      loanReadyGrats: "",
      accountStatus: "",
      salesManager: "",
      bhAdmin: "",
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== "").length;

  const handleAddClient = async () => {
    if (!newClient.first_name || !newClient.last_name) {
      toast.error("First name and last name are required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/clients/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newClient),
      });

      const responseText = await response.text().catch(() => "");
      let data = null;
      try { data = JSON.parse(responseText); } catch {}

      if (response.ok || (data && data.success)) {
        toast.success("Client created successfully");
        setAddClientDialogOpen(false);
        setNewClient({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          date_of_birth: "",
          address1: "",
          city: "",
          state: "",
          postal_code: "",
          betenbough_location: "",
          betenbough_sales_rep: user?.name || "",
          betenbough_client_id_number: "",
          betenbough_affiliated_lender: "",
          betenbough_assigned_credit_counselor: "",
          betenbough_special_notes_or_credit_strategy: "",
          betenbough_deadline_date_for_loan_ready: "",
        });
        fetchClients();
        navigate(`${isAdmin ? "/admin" : "/team"}/clients/${data.client_id}`);
      } else if (response.status === 401) {
        toast.error("Your session has expired. Please log in again.");
        navigate("/login");
      } else {
        console.error(`Create client failed: HTTP ${response.status}`, responseText);
        const detail = data?.detail;
        const errorMsg = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join("; ")
          : `Server error (${response.status}). Please try again.`;
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Add client error:", error);
      toast.error(`Failed to create client: ${error.message || "Unknown error"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const loadEditOptions = async () => {
    if (!canEditClients || editCounselors.length) return;
    const [l, c] = await Promise.all([
      authFetch(`${API}/lookup/preferred-lenders`).then((r) => r.json()).catch(() => ({})),
      authFetch(`${API}/lookup/crm-counselors`, { credentials: "include" }).then((r) => r.json()).catch(() => ({})),
    ]);
    setEditLenders(l.lenders || []);
    setEditCounselors(c.counselors || []);
  };

  // Open edit dialog
  const openEditDialog = (client, e) => {
    e.stopPropagation();
    loadEditOptions();
    setEditingClient(client);
    setEditFormData({
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      email: client.email || "",
      phone: client.phone || "",
      betenbough_location: client.betenbough_location || (client.betenbough_locations || "").split(",")[0].trim(),
      betenbough_sales_rep: client.betenbough_sales_rep || "",
      betenbough_affiliate_2: client.betenbough_affiliate_2 || "",
      betenbough_affiliated_lender: client.betenbough_affiliated_lender || "",
      assigned_counselor: client.assigned_counselor || client.betenbough_assigned_credit_counselor || "",
      betenbough_deadline_date_for_loan_ready: client.betenbough_deadline_date_for_loan_ready || "",
    });
    setEditDialogOpen(true);
  };

  // Handle edit submit
  const handleEditClient = async () => {
    if (!editingClient) return;
    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/clients/${editingClient.client_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editFormData),
      });

      const respText = await response.text().catch(() => "");
      let respData = null;
      try { respData = JSON.parse(respText); } catch {}

      if (response.ok) {
        toast.success(respData?.message === "No changes" ? "No changes to save" : "Saved to the CRM");
        setEditDialogOpen(false);
        setEditingClient(null);
        fetchClients();
      } else {
        console.error(`Edit client failed: HTTP ${response.status}`, respText);
        const detail = respData?.detail;
        const errorMsg = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join("; ")
          : `Server error (${response.status}). Please try again.`;
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Edit client error:", error);
      toast.error(`Failed to update client: ${error.message || "Unknown error"}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete confirmation
  const openDeleteDialog = (client, e) => {
    e.stopPropagation();
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/clients/${clientToDelete.client_id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Client deleted successfully");
        setDeleteDialogOpen(false);
        setClientToDelete(null);
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete client");
      }
    } catch (error) {
      console.error("Delete client error:", error);
      toast.error("Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  const basePath = isAdmin ? "/admin" : "/team";

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} />

      <main className="flex-1 min-w-0 md:ml-64">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 backdrop-blur-md border-b transition-colors duration-300 ${isDarkMode ? 'bg-black/90 border-[#1a3d18]' : 'bg-white/90 border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>All Clients</h1>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {totalClients} total clients
                </p>
              </div>
            </div>
            {user?.role === "CREDITFIRMINC_ADMIN" && <LocationReportButton />}
            <Dialog open={addClientDialogOpen} onOpenChange={setAddClientDialogOpen}>
              <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#265324] border-[#1a3d18] text-white' : ''}`}>
                <DialogHeader>
                  <DialogTitle className={isDarkMode ? 'text-white' : ''}>Add New Client</DialogTitle>
                  <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Manually add a client to the portal
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>First Name *</Label>
                      <Input
                        value={newClient.first_name}
                        onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="add-client-first-name"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Last Name *</Label>
                      <Input
                        value={newClient.last_name}
                        onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="add-client-last-name"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Email</Label>
                      <Input
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                      <Input
                        value={newClient.phone}
                        onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Date of Birth</Label>
                      <Input
                        type="date"
                        value={newClient.date_of_birth}
                        onChange={(e) => setNewClient({ ...newClient, date_of_birth: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Select
                        value={newClient.betenbough_location}
                        onValueChange={(v) => setNewClient({ ...newClient, betenbough_location: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {BH_OFFICES.map((loc) => (
                            <SelectItem key={loc} value={loc}>
                              {loc}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={newClient.address1}
                      onChange={(e) => setNewClient({ ...newClient, address1: e.target.value })}
                      className="mt-1"
                      placeholder="Street address"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={newClient.city}
                        onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={newClient.state}
                        onChange={(e) => setNewClient({ ...newClient, state: e.target.value })}
                        className="mt-1"
                        placeholder="TX"
                      />
                    </div>
                    <div>
                      <Label>ZIP</Label>
                      <Input
                        value={newClient.postal_code}
                        onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Affiliate Partner</Label>
                      <Input
                        value={newClient.betenbough_sales_rep}
                        onChange={(e) => setNewClient({ ...newClient, betenbough_sales_rep: e.target.value })}
                        className="mt-1"
                        placeholder={user?.name || "Your name"}
                      />
                    </div>
                    <div>
                      <Label>Client ID</Label>
                      <Input
                        value={newClient.betenbough_client_id_number}
                        onChange={(e) => setNewClient({ ...newClient, betenbough_client_id_number: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Affiliated Lender</Label>
                      <Input
                        value={newClient.betenbough_affiliated_lender}
                        onChange={(e) => setNewClient({ ...newClient, betenbough_affiliated_lender: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Assigned Counselor</Label>
                      <Input
                        value={newClient.betenbough_assigned_credit_counselor}
                        onChange={(e) => setNewClient({ ...newClient, betenbough_assigned_credit_counselor: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Target Loan-Ready Date</Label>
                      <Input
                        type="date"
                        value={newClient.betenbough_deadline_date_for_loan_ready}
                        onChange={(e) => setNewClient({ ...newClient, betenbough_deadline_date_for_loan_ready: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Special Notes / Credit Strategy</Label>
                    <Textarea
                      value={newClient.betenbough_special_notes_or_credit_strategy}
                      onChange={(e) => setNewClient({ ...newClient, betenbough_special_notes_or_credit_strategy: e.target.value })}
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddClientDialogOpen(false)} className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddClient} disabled={submitting} data-testid="submit-add-client" className="bg-red-600 hover:bg-red-700 text-white">
                    {submitting ? "Creating..." : "Create Client"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="p-4 md:p-6">
          <Card className={`transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="clients-table-card">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Client List</CardTitle>
                    <Badge variant="secondary" className={isDarkMode ? 'bg-[#22c55e]/20 text-[#22c55e]' : ''}>
                      {totalClients} total
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search name, email, phone..."
                        className={`pl-9 w-full md:w-56 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        data-testid="search-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className={`${isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}`}
                    >
                      <Filter className="h-4 w-4 mr-1" />
                      Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                    </Button>
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className={`${isDarkMode ? 'text-slate-300 hover:text-white' : ''}`}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Advanced Filters */}
                {showAdvancedFilters && (
                  <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 pt-4 border-t ${isDarkMode ? 'border-[#1a3d18]' : 'border-slate-200'}`}>
                    <SearchableSelect
                      options={filterOptions.locations}
                      value={filters.location}
                      onValueChange={(v) => setFilters({ ...filters, location: v })}
                      placeholder="Location"
                      darkMode={isDarkMode}
                      data-testid="location-filter"
                    />
                    
                    <SearchableSelect
                      options={filterOptions.counselors}
                      value={filters.counselor}
                      onValueChange={(v) => setFilters({ ...filters, counselor: v })}
                      placeholder="Counselor"
                      darkMode={isDarkMode}
                      data-testid="counselor-filter"
                    />
                    
                    <SearchableSelect
                      options={filterOptions.affiliates}
                      value={filters.affiliate}
                      onValueChange={(v) => setFilters({ ...filters, affiliate: v })}
                      placeholder="Affiliate"
                      darkMode={isDarkMode}
                      data-testid="affiliate-filter"
                    />
                    
                    <Select
                      value={filters.leadStatus}
                      onValueChange={(v) => setFilters({ ...filters, leadStatus: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className={`${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="lead-status-filter">
                        <SelectValue placeholder="Lead Status" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All Lead Statuses</SelectItem>
                        {filterOptions.leadStatuses.map((s, idx) => (
                          <SelectItem key={idx} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={filters.loanReadyStage}
                      onValueChange={(v) => setFilters({ ...filters, loanReadyStage: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className={`${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="loan-ready-filter">
                        <SelectValue placeholder="Loan Ready Stage" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All Loan Ready Stages</SelectItem>
                        {filterOptions.loanReadyStages.map((s, idx) => (
                          <SelectItem key={idx} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={filters.rounds}
                      onValueChange={(v) => setFilters({ ...filters, rounds: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className={`${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="rounds-filter">
                        <SelectValue placeholder="Credit Rounds" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All Rounds</SelectItem>
                        {filterOptions.rounds.map((r, idx) => (
                          <SelectItem key={idx} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={filters.loanReadyGrats}
                      onValueChange={(v) => setFilters({ ...filters, loanReadyGrats: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className={`${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="grats-filter">
                        <SelectValue placeholder="Loan Ready Grats" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All Grats Stages</SelectItem>
                        {filterOptions.loanReadyGrats.map((g, idx) => (
                          <SelectItem key={idx} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.accountStatus}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, accountStatus: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''} data-testid="filter-account-status">
                        <SelectValue placeholder="Account Status" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All Account Statuses</SelectItem>
                        {filterOptions.accountStatuses.map((s, idx) => (
                          <SelectItem key={idx} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <SearchableSelect
                      options={filterOptions.salesManagers}
                      value={filters.salesManager}
                      onValueChange={(v) => setFilters({ ...filters, salesManager: v })}
                      placeholder="Sales Manager"
                      darkMode={isDarkMode}
                      data-testid="sales-manager-filter"
                    />

                    <Select
                      value={filters.bhAdmin}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, bhAdmin: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''} data-testid="bh-admin-filter">
                        <SelectValue placeholder="BH Admin" />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[300px] ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}`}>
                        <SelectItem value="all">All BH Admins</SelectItem>
                        {filterOptions.bhAdmins.map((a) => (
                          <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={isDarkMode ? 'border-[#1a3d18]' : ''}>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Date Created</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Name</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Affiliate</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Loan Ready Status</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Location</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Sales Manager</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Counselor</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Status</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Account Status</TableHead>
                      <TableHead className={`text-right ${isDarkMode ? 'text-slate-300' : ''}`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i} className={isDarkMode ? 'border-slate-800' : ''}>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-6 w-28" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-8 w-16 ml-auto" /></TableCell>
                        </TableRow>
                      ))
                    ) : clients.length > 0 ? (
                      clients.map((client) => (
                        <TableRow
                          key={client.client_id}
                          className={`cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 border-[#1a3d18]' : 'hover:bg-slate-50'}`}
                          onClick={() => navigate(`${basePath}/clients/${client.client_id}`)}
                        >
                          <TableCell className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {client.ghl_created_at ? new Date(client.ghl_created_at).toLocaleDateString() : client.created_at ? new Date(client.created_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className={`font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                            <span className="flex items-center gap-1.5">
                              {client.first_name} {client.last_name}
                              {client.ghl_contact_id ? (
                                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" title="Synced to CRM" data-testid={`sync-ok-${client.client_id}`} />
                              ) : client.sync_status === "failed" ? (
                                <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="CRM sync failed" data-testid={`sync-fail-${client.client_id}`} />
                              ) : (
                                <span className="w-2 h-2 rounded-full bg-yellow-500 shrink-0 animate-pulse" title="Syncing to CRM..." data-testid={`sync-pending-${client.client_id}`} />
                              )}
                            </span>
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            {client.betenbough_sales_rep || client.affiliate_1_name || client.affiliate_1_full_name || client.sales_rep || "-"}
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              client.rescore_status 
                                ? isDarkMode ? 'bg-[#22c55e]/20 text-[#84c77f]' : 'bg-emerald-50 text-emerald-700'
                                : ''
                            }`}>
                              {client.rescore_status || "-"}
                            </span>
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            {client.betenbough_locations || client.betenbough_location || ""}
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            {client.corporate_manager || "-"}
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            {client.assigned_counselor || client.betenbough_assigned_credit_counselor || "-"}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={client.lead_status_amp_stages || client.client_account_status || client.betenbough_client_status} />
                          </TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{client.client_account_status || client.clients_acct_status || "-"}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={isDarkMode ? 'text-slate-300 hover:text-white hover:bg-[#22c55e]/20' : ''}
                                onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/clients/${client.client_id}`); }}
                                data-testid={`view-client-${client.client_id}`}
                              >
                                View <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${isDarkMode ? 'text-slate-300 hover:text-white hover:bg-[#22c55e]/20' : ''}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                                  <DropdownMenuItem 
                                    onClick={(e) => openEditDialog(client, e)}
                                    className={isDarkMode ? 'text-slate-200 focus:bg-[#22c55e]/20' : ''}
                                    data-testid={`edit-client-${client.client_id}`}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  {isAdmin && (
                                    <DropdownMenuItem 
                                      onClick={(e) => openDeleteDialog(client, e)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                      data-testid={`delete-client-${client.client_id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow className={isDarkMode ? 'border-[#1a3d18]' : ''}>
                        <TableCell colSpan={6} className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          No clients found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination Controls */}
              {totalClients > pageSize && (
                <div className={`flex items-center justify-between pt-4 border-t mt-4 ${isDarkMode ? 'border-[#1a3d18]' : 'border-slate-200'}`}>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalClients)} of {totalClients} clients
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20 disabled:opacity-50' : ''}
                    >
                      Previous
                    </Button>
                    <div className={`flex items-center gap-1 ${isDarkMode ? 'text-slate-300' : ''}`}>
                      {Array.from({ length: Math.min(5, Math.ceil(totalClients / pageSize)) }, (_, i) => {
                        const totalPages = Math.ceil(totalClients / pageSize);
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 p-0 ${currentPage === pageNum 
                              ? 'bg-[#22c55e] hover:bg-[#16a34a] text-white' 
                              : isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalClients / pageSize), p + 1))}
                      disabled={currentPage >= Math.ceil(totalClients / pageSize)}
                      className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20 disabled:opacity-50' : ''}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Edit Client Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#265324] border-[#1a3d18] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Edit Client</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
              {canEditClients ? "Changes save straight to the CRM contact." : "Read only — only Credit Firm admins and counselors can edit. Changes made in the CRM sync back automatically."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-1">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>First Name</Label>
                <Input
                  value={editFormData.first_name || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                  data-testid="edit-client-first-name"
                />
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Last Name</Label>
                <Input
                  value={editFormData.last_name || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                  data-testid="edit-client-last-name"
                />
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-3">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Email</Label>
                <Input
                  type="email"
                  value={editFormData.email || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                />
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-4">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                <Input
                  value={editFormData.phone || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                />
              </ReadOnly>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-5">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Location</Label>
                <Select
                  value={BH_LOCATION_OPTIONS.find((o) => o.label === bhLocationShort(editFormData.betenbough_location))?.value || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, betenbough_location: v })}
                >
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                    {BH_LOCATION_OPTIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-6">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Target Loan-Ready Date</Label>
                <Input
                  type="date"
                  value={editFormData.betenbough_deadline_date_for_loan_ready || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, betenbough_deadline_date_for_loan_ready: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                />
              </ReadOnly>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-7">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Affiliate Partner</Label>
                <SearchableSelect
                  options={filterOptions.affiliates}
                  value={editFormData.betenbough_sales_rep || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, betenbough_sales_rep: v })}
                  placeholder="Select affiliate"
                  darkMode={isDarkMode}
                  data-testid="edit-client-affiliate-1"
                />
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-8">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Affiliate 2</Label>
                <SearchableSelect
                  options={filterOptions.affiliates}
                  value={editFormData.betenbough_affiliate_2 || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, betenbough_affiliate_2: v })}
                  placeholder="Select affiliate"
                  darkMode={isDarkMode}
                  data-testid="edit-client-affiliate-2"
                />
              </ReadOnly>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-9">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Affiliated Lender</Label>
                <SearchableSelect
                  options={editLenders}
                  value={editFormData.betenbough_affiliated_lender || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, betenbough_affiliated_lender: v })}
                  placeholder="Select lender"
                  darkMode={isDarkMode}
                  data-testid="edit-client-lender"
                />
              </ReadOnly>
              <ReadOnly locked={!canEditClients} testId="edit-client-readonly-10">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Counselor</Label>
                <SearchableSelect
                  options={editCounselors}
                  value={editFormData.assigned_counselor || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, assigned_counselor: v })}
                  placeholder="Select counselor"
                  darkMode={isDarkMode}
                  data-testid="edit-client-counselor"
                />
              </ReadOnly>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>
              Cancel
            </Button>
            <ReadOnly locked={!canEditClients} testId="submit-edit-client-readonly">
              <Button onClick={handleEditClient} disabled={submitting || !canEditClients} data-testid="submit-edit-client" className="bg-[#22c55e] hover:bg-[#16a34a] text-white">
                {submitting ? "Saving to CRM..." : "Save Changes"}
              </Button>
            </ReadOnly>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDarkMode ? 'text-white' : ''}>Delete Client</AlertDialogTitle>
            <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
              Are you sure you want to delete <span className="font-semibold">{clientToDelete?.first_name} {clientToDelete?.last_name}</span>? 
              This action cannot be undone and will remove all associated notes, tasks, and documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-client"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AllClients;
